import React from 'react';
import { useNavigate } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import { theme } from '../../../styles/theme';

/* ═══════════════════════════════════════════════════════════════
   HOW IT WORKS — 6-step walkthrough with accurate app mockups
   ═══════════════════════════════════════════════════════════════ */

// ─── Animations ───────────────────────────────────────────────
const fadeInUp = keyframes`
  from { opacity: 0; transform: translateY(30px); }
  to   { opacity: 1; transform: translateY(0); }
`;

// ─── Page Layout ──────────────────────────────────────────────
const Page = styled.div`
  min-height: 100vh;
  background: ${theme.colors.background};
  overflow-x: hidden;
`;

const Hero = styled.section`
  background: ${theme.colors.deepNavy};
  color: ${theme.colors.white};
  text-align: center;
  padding: 56px 20px 52px;
  position: relative;
  &::after {
    content: '';
    position: absolute;
    bottom: -1px;
    left: 0;
    right: 0;
    height: 36px;
    background: ${theme.colors.background};
    clip-path: ellipse(52% 100% at 50% 100%);
  }
`;

const HeroLogo = styled.img`
  width: 52px;
  height: 52px;
  margin-bottom: 14px;
  animation: ${fadeInUp} 0.6s ease-out;
`;

const HeroH1 = styled.h1`
  font-family: ${theme.typography.headingFont};
  font-size: 26px;
  font-weight: 700;
  margin-bottom: 8px;
  animation: ${fadeInUp} 0.6s ease-out 0.1s both;
`;

const Gold = styled.span`color: ${theme.colors.brandYellow};`;

const HeroSub = styled.p`
  font-family: ${theme.typography.bodyFont};
  font-size: 14px;
  color: rgba(255,255,255,0.72);
  max-width: 320px;
  margin: 0 auto;
  line-height: 1.55;
  animation: ${fadeInUp} 0.6s ease-out 0.2s both;
`;

// ─── Steps Container ─────────────────────────────────────────
const Steps = styled.section`
  padding: 36px 16px 16px;
  max-width: 460px;
  margin: 0 auto;
`;

const Pill = styled.div`
  text-align: center;
  margin-bottom: 32px;
  animation: ${fadeInUp} 0.6s ease-out 0.3s both;
`;

const PillTag = styled.span`
  font-family: ${theme.typography.headingFont};
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 2.5px;
  color: ${theme.colors.slateGray};
  background: white;
  padding: 5px 14px;
  border-radius: 20px;
  border: 1px solid ${theme.colors.softGray};
`;

// ─── Step Card ────────────────────────────────────────────────
const StepWrap = styled.div`
  margin-bottom: 44px;
  animation: ${fadeInUp} 0.6s ease-out ${({ $d }) => $d} both;
`;

const StepHead = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 14px;
`;

const Num = styled.div`
  width: 34px;
  height: 34px;
  border-radius: 50%;
  background: ${theme.colors.deepNavy};
  color: ${theme.colors.brandYellow};
  font-family: ${theme.typography.headingFont};
  font-size: 14px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  margin-top: 1px;
`;

const StepInfo = styled.div`flex: 1;`;

const StepH = styled.h3`
  font-family: ${theme.typography.headingFont};
  font-size: 16px;
  font-weight: 700;
  color: ${theme.colors.deepNavy};
  margin-bottom: 2px;
`;

const StepP = styled.p`
  font-family: ${theme.typography.bodyFont};
  font-size: 12.5px;
  color: ${theme.colors.slateGray};
  line-height: 1.5;
`;

// ─── Phone Frame ──────────────────────────────────────────────
const Phone = styled.div`
  width: 270px;
  margin: 0 auto;
  background: white;
  border-radius: 34px;
  border: 3px solid #1a1a2e;
  padding: 12px 8px 8px;
  box-shadow: 0 20px 44px rgba(0,0,51,0.13), 0 3px 10px rgba(0,0,51,0.07);
  &::before {
    content: '';
    display: block;
    width: 72px;
    height: 5px;
    background: #1a1a2e;
    border-radius: 3px;
    margin: 0 auto 8px;
  }
`;

const Screen = styled.div`
  width: 100%;
  aspect-ratio: 9/17.5;
  border-radius: 22px;
  overflow: hidden;
  background: #f9f9f9;
  display: flex;
  flex-direction: column;
  position: relative;
`;

const HomeBar = styled.div`
  width: 90px;
  height: 4px;
  background: #1a1a2e;
  border-radius: 2px;
  margin: 6px auto 2px;
  opacity: 0.2;
`;

// ─── Shared Mock Elements ─────────────────────────────────────
const TopBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 9px 12px;
  background: white;
  border-bottom: 1px solid #eee;
  position: relative;
`;

const BackArrow = styled.span`
  position: absolute;
  left: 12px;
  font-size: 13px;
  color: ${theme.colors.deepNavy};
`;

const BarTitle = styled.span`
  font-family: ${theme.typography.headingFont};
  font-size: 11px;
  font-weight: 700;
  color: ${theme.colors.deepNavy};
`;

const Body = styled.div`
  flex: 1;
  padding: 10px 11px;
  display: flex;
  flex-direction: column;
  gap: ${({ $gap }) => $gap || '6px'};
  overflow: hidden;
`;

const Sub = styled.div`
  font-family: ${theme.typography.bodyFont};
  font-size: 8.5px;
  color: #555;
  margin-bottom: 2px;
`;

const BtnY = styled.div`
  padding: 8px;
  background: ${theme.colors.brandYellow};
  color: ${theme.colors.deepNavy};
  font-family: ${theme.typography.headingFont};
  font-size: 9.5px;
  font-weight: 700;
  text-transform: uppercase;
  text-align: center;
  border-radius: 10px;
  margin-top: auto;
`;

// Bottom nav bar mock
const NavBar = styled.div`
  display: flex;
  justify-content: space-around;
  align-items: center;
  height: 36px;
  background: white;
  border-top: 1px solid #eee;
  flex-shrink: 0;
`;

const NavDot = styled.div`
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: ${({ $active }) => $active ? 'rgba(255,209,0,0.8)' : '#eee'};
`;

// ─── Screen 1: Cause Selection ────────────────────────────────
const CCard = styled.div`
  display: flex;
  align-items: center;
  padding: 9px 10px;
  background: white;
  border: 1.5px solid ${({ $on }) => $on ? '#ffcc00' : '#ddd'};
  border-radius: 10px;
  gap: 9px;
  ${({ $on }) => $on && 'box-shadow: 0 0 5px rgba(255,204,0,0.45);'}
`;

const CIcon = styled.img`
  width: 32px;
  height: 32px;
  flex-shrink: 0;
  border-radius: 4px;
`;

const CInfo = styled.div`flex: 1; min-width: 0;`;

const CName = styled.div`
  font-family: ${theme.typography.headingFont};
  font-size: 9px;
  font-weight: 700;
  color: ${theme.colors.deepNavy};
`;

const CDesc = styled.div`
  font-family: ${theme.typography.bodyFont};
  font-size: 7px;
  color: #666;
  line-height: 1.3;
  margin-top: 1px;
`;

const Radio = styled.div`
  width: 16px;
  height: 16px;
  border-radius: 50%;
  border: 2px solid ${({ $on }) => $on ? '#ffcc00' : '#ccc'};
  background: ${({ $on }) => $on ? '#ffcc00' : 'white'};
  flex-shrink: 0;
`;

const Screen1 = () => (
  <>
    <TopBar>
      <BackArrow>&larr;</BackArrow>
      <BarTitle>Cause Selection</BarTitle>
    </TopBar>
    <Body>
      <Sub>Choose one or more causes you care about:</Sub>
      <CCard $on>
        <CIcon src="/images/cause_homeless_awareness.png" alt="" />
        <CInfo>
          <CName>Homelessness Awareness</CName>
          <CDesc>Track outreach, resource sharing, and policy advocacy.</CDesc>
        </CInfo>
        <Radio $on />
      </CCard>
      <CCard $on>
        <CIcon src="/images/cause_comunity_beautification.png" alt="" />
        <CInfo>
          <CName>Community Beautification</CName>
          <CDesc>Participate in cleanup efforts and local projects.</CDesc>
        </CInfo>
        <Radio $on />
      </CCard>
      <CCard>
        <CIcon src="/images/cause_human_traffic.png" alt="" />
        <CInfo>
          <CName>Human Trafficking Awareness</CName>
          <CDesc>Combat trafficking through education and support.</CDesc>
        </CInfo>
        <Radio />
      </CCard>
      <div style={{ fontSize: '7px', color: '#555', textAlign: 'center', marginTop: '6px' }}>
        You can add or remove causes later from the home screen.
      </div>
      <BtnY>Continue</BtnY>
    </Body>
    <NavBar><NavDot /><NavDot /><NavDot /><NavDot /></NavBar>
  </>
);

// ─── Screen 2: Level Selection ────────────────────────────────
const LCard = styled.div`
  display: flex;
  align-items: center;
  padding: 9px 10px;
  background: white;
  border: 1.5px solid ${({ $on }) => $on ? '#ffcc00' : '#ddd'};
  border-radius: 14px;
  gap: 9px;
`;

const LIcon = styled.img`
  width: 28px;
  height: 28px;
  flex-shrink: 0;
`;

const LInfo = styled.div`flex: 1;`;

const LName = styled.div`
  font-family: ${theme.typography.headingFont};
  font-size: 9px;
  font-weight: 700;
  color: ${theme.colors.deepNavy};
`;

const LDesc = styled.div`
  font-family: ${theme.typography.bodyFont};
  font-size: 7px;
  color: ${theme.colors.slateGray};
  margin-top: 1px;
`;

const LEg = styled.div`
  font-family: ${theme.typography.bodyFont};
  font-size: 6.5px;
  color: ${theme.colors.slateGray};
  margin-top: 1px;
`;

const Screen2 = () => (
  <>
    <TopBar>
      <BackArrow>&larr;</BackArrow>
      <BarTitle>Choose Your Level</BarTitle>
    </TopBar>
    <Body>
      <div style={{ textAlign: 'center', marginBottom: '2px' }}>
        <LIcon src="/images/cause_homeless_awareness.png" alt="" style={{ width: 22, height: 22 }} />
        <div style={{ fontFamily: theme.typography.headingFont, fontSize: '9px', fontWeight: 700, color: theme.colors.deepNavy }}>
          For: Homelessness Awareness
        </div>
      </div>
      <Sub>Select the level that best describes your current advocacy:</Sub>
      <LCard>
        <LIcon src="/icons/icon_streetlight.png" alt="" />
        <LInfo>
          <LName>Level 1 - Streetlight Advocate</LName>
          <LDesc>I'm just getting started or prefer small, personal acts.</LDesc>
          <LEg>Examples: sharing resources, learning, donating</LEg>
        </LInfo>
        <Radio />
      </LCard>
      <LCard $on>
        <LIcon src="/icons/icon_daylight.png" alt="" />
        <LInfo>
          <LName>Level 2 - Daylight Advocate</LName>
          <LDesc>I'm ready for more visible, collaborative community work.</LDesc>
          <LEg>Examples: organizing events, community action</LEg>
        </LInfo>
        <Radio $on />
      </LCard>
      <LCard>
        <LIcon src="/icons/icon_sunshield.png" alt="" />
        <LInfo>
          <LName>Level 3 - Sunshield Advocate</LName>
          <LDesc>I'm working on long-term, strategic systems change.</LDesc>
          <LEg>Examples: policy work, coalition-building</LEg>
        </LInfo>
        <Radio />
      </LCard>
      <div style={{ fontSize: '7px', color: theme.colors.slateGray, textAlign: 'center', marginTop: 'auto', paddingTop: '4px' }}>
        You can stay at any level indefinitely. Change later if needed.
      </div>
      <BtnY>Confirm</BtnY>
    </Body>
    <NavBar><NavDot /><NavDot /><NavDot /><NavDot /></NavBar>
  </>
);

// ─── Screen 3: Log Activity ──────────────────────────────────
const Input = styled.div`
  padding: 8px 10px;
  background: white;
  border: 1px solid ${theme.colors.softGray};
  border-radius: 8px;
  font-family: ${theme.typography.bodyFont};
  font-size: 8.5px;
  color: ${({ $ph }) => $ph ? '#999' : theme.colors.deepNavy};
`;

const TextArea = styled(Input)`
  min-height: 40px;
  line-height: 1.35;
`;

const DropZone = styled.div`
  height: 52px;
  border: 1.5px dashed #ccc;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: white;
`;

const PlusCircle = styled.div`
  width: 24px;
  height: 24px;
  border-radius: 50%;
  border: 1px solid #ccc;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  color: #ccc;
`;

const CauseSel = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 10px;
  background: white;
  border: 1px solid ${({ $on }) => $on ? theme.colors.brandYellow : theme.colors.softGray};
  border-radius: 8px;
`;

const CSelName = styled.div`
  font-family: ${theme.typography.headingFont};
  font-size: 9px;
  font-weight: 700;
  color: ${theme.colors.deepNavy};
`;

const CSelLevel = styled.div`
  font-family: ${theme.typography.bodyFont};
  font-size: 7px;
  color: ${theme.colors.slateGray};
`;

const SmallRadio = styled.div`
  width: 14px;
  height: 14px;
  border-radius: 50%;
  border: 2px solid ${({ $on }) => $on ? theme.colors.brandYellow : '#ccc'};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const SmallDot = styled.div`
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: ${theme.colors.brandYellow};
`;

const Label = styled.div`
  font-family: ${theme.typography.bodyFont};
  font-size: 8px;
  color: ${theme.colors.slateGray};
  margin-top: 2px;
`;

const Screen3 = () => (
  <>
    <TopBar>
      <BackArrow>&larr;</BackArrow>
      <BarTitle>Log Activity</BarTitle>
    </TopBar>
    <Body $gap="5px">
      <Label>Which cause?</Label>
      <CauseSel $on>
        <div><CSelName>Homelessness Awareness</CSelName><CSelLevel>Level 2 - Daylight</CSelLevel></div>
        <SmallRadio $on><SmallDot /></SmallRadio>
      </CauseSel>
      <CauseSel>
        <div><CSelName>Community Beautification</CSelName><CSelLevel>Level 2 - Daylight</CSelLevel></div>
        <SmallRadio />
      </CauseSel>
      <Label>What did you do?</Label>
      <Input>Organized winter supply drive at shelter</Input>
      <Label>Add a photo</Label>
      <DropZone><PlusCircle>+</PlusCircle></DropZone>
      <Label>Reflection (optional)</Label>
      <TextArea>
        Connected with 15 community members and collected coats, blankets, and hygiene kits for the downtown shelter.
      </TextArea>
      <BtnY>Log Activity</BtnY>
    </Body>
    <NavBar><NavDot /><NavDot $active /><NavDot /><NavDot /></NavBar>
  </>
);

// ─── Screen 4: Progress ──────────────────────────────────────
const Chip = styled.div`
  padding: 3px 8px;
  font-family: ${theme.typography.bodyFont};
  font-size: 7.5px;
  border-radius: 14px;
  background: ${({ $on }) => $on ? '#fdd835' : '#eee'};
  color: ${({ $on }) => $on ? '#333' : '#888'};
  white-space: nowrap;
`;

const ACard = styled.div`
  padding: 9px 10px;
  background: white;
  border-radius: 10px;
  border: 1px solid #f0f0f0;
  box-shadow: 0 2px 4px rgba(0,0,0,0.04);
`;

const ATitle = styled.div`
  font-family: ${theme.typography.headingFont};
  font-size: 9.5px;
  font-weight: 700;
  color: ${theme.colors.deepNavy};
`;

const AMeta = styled.div`
  font-family: ${theme.typography.bodyFont};
  font-size: 7px;
  color: ${theme.colors.slateGray};
  margin-top: 2px;
`;

const AReflection = styled.div`
  font-family: ${theme.typography.bodyFont};
  font-size: 7px;
  color: #555;
  margin-top: 4px;
  line-height: 1.35;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const Screen4 = () => (
  <>
    <TopBar>
      <BackArrow>&larr;</BackArrow>
      <BarTitle>My Progress</BarTitle>
    </TopBar>
    <Body $gap="6px">
      <div style={{ display: 'flex', gap: '3px', flexWrap: 'wrap' }}>
        <Chip $on>All</Chip>
        <Chip>Homelessness</Chip>
        <Chip>Beautification</Chip>
        <Chip>Trafficking</Chip>
      </div>
      <div style={{ display: 'flex', gap: '3px' }}>
        <Chip>Streetlight</Chip>
        <Chip>Daylight</Chip>
        <Chip>Sunshield</Chip>
      </div>
      <ACard>
        <ATitle>Organized winter supply drive</ATitle>
        <AMeta>Feb 20, 2026 &middot; Homelessness &middot; Daylight</AMeta>
        <AReflection>Connected with 15 community members and collected coats, blankets, and hygiene kits...</AReflection>
      </ACard>
      <ACard>
        <ATitle>Painted mural at community center</ATitle>
        <AMeta>Feb 16, 2026 &middot; Beautification &middot; Daylight</AMeta>
        <AReflection>Spent the afternoon with local artists creating a mural celebrating our neighborhood...</AReflection>
      </ACard>
      <ACard>
        <ATitle>Hosted awareness workshop</ATitle>
        <AMeta>Feb 10, 2026 &middot; Trafficking &middot; Sunshield</AMeta>
        <AReflection>Presented to 30 attendees on recognizing signs and reporting resources...</AReflection>
      </ACard>
      <ACard>
        <ATitle>Donated toiletry bags</ATitle>
        <AMeta>Feb 5, 2026 &middot; Homelessness &middot; Streetlight</AMeta>
      </ACard>
    </Body>
    <NavBar><NavDot /><NavDot /><NavDot $active /><NavDot /></NavBar>
  </>
);

// ─── Screen 5: Profile & Goals ────────────────────────────────
const StatGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 4px;
  padding: 10px 8px;
  background: #1a1a2e;
  border-radius: 10px;
`;

const StatNum = styled.div`
  font-size: 14px;
  font-weight: 700;
  color: white;
  text-align: center;
`;

const StatLbl = styled.div`
  font-size: 6px;
  color: #d0d6e8;
  text-align: center;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  margin-top: 1px;
`;

const GoalCard = styled.div`
  background: white;
  border: 1px solid #eee;
  border-radius: 8px;
  padding: 8px 10px;
`;

const GoalHeader = styled.div`
  font-family: ${theme.typography.headingFont};
  font-size: 9px;
  font-weight: 700;
  color: ${theme.colors.deepNavy};
  margin-bottom: 6px;
`;

const GoalRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 4px 0;
`;

const GoalName = styled.span`
  font-family: ${theme.typography.bodyFont};
  font-size: 8px;
  color: ${theme.colors.deepNavy};
`;

const GoalCount = styled.span`
  font-family: ${theme.typography.bodyFont};
  font-size: 8px;
  font-weight: 600;
  color: ${({ $met }) => $met ? '#4CAF50' : theme.colors.deepNavy};
`;

const GoalBar = styled.div`
  width: 100%;
  height: 3px;
  background: #E0E0E0;
  border-radius: 2px;
  overflow: hidden;
  margin-top: 2px;
  margin-bottom: 6px;
`;

const GoalFill = styled.div`
  height: 100%;
  border-radius: 2px;
  background: ${({ $color }) => $color};
  width: ${({ $pct }) => $pct}%;
`;

const ElevBtn = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  padding: 7px;
  background: ${theme.colors.brandYellow};
  border-radius: 10px;
  font-family: ${theme.typography.headingFont};
  font-size: 9px;
  font-weight: 700;
  color: #1a1a2e;
  letter-spacing: 0.3px;
`;

const Screen5 = () => (
  <>
    <TopBar>
      <BarTitle>Profile</BarTitle>
    </TopBar>
    <Body $gap="7px">
      {/* Avatar + name */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', background: 'white', padding: '10px', borderRadius: '10px' }}>
        <div style={{ width: '34px', height: '34px', borderRadius: '50%', background: '#e0e0e0', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '14px', color: '#333', fontWeight: 700, flexShrink: 0 }}>
          M
        </div>
        <div>
          <div style={{ fontFamily: theme.typography.headingFont, fontSize: '10px', fontWeight: 700, color: theme.colors.deepNavy }}>Maya Johnson</div>
          <div style={{ fontFamily: theme.typography.bodyFont, fontSize: '7px', color: '#666' }}>Member since September 2025</div>
        </div>
      </div>
      {/* Stats */}
      <StatGrid>
        <div><StatNum>12</StatNum><StatLbl>Total</StatLbl></div>
        <div><StatNum>4</StatNum><StatLbl>Street</StatLbl></div>
        <div><StatNum>6</StatNum><StatLbl>Day</StatLbl></div>
        <div><StatNum>2</StatNum><StatLbl>Sun</StatLbl></div>
      </StatGrid>
      {/* Monthly Goals */}
      <GoalCard>
        <GoalHeader>Monthly Goals: February 2026</GoalHeader>
        <GoalRow>
          <GoalName>Homelessness Awareness</GoalName>
          <GoalCount $met>3 / 3</GoalCount>
        </GoalRow>
        <GoalBar><GoalFill $color="#4CAF50" $pct={100} /></GoalBar>
        <GoalRow>
          <GoalName>Community Beautification</GoalName>
          <GoalCount>2 / 4</GoalCount>
        </GoalRow>
        <GoalBar><GoalFill $color="#FFD100" $pct={50} /></GoalBar>
        <div style={{ borderTop: '1px solid #f0f0f0', paddingTop: '5px', marginTop: '2px', display: 'flex', justifyContent: 'space-between' }}>
          <span style={{ fontSize: '7px', color: '#808080' }}>5 of 7 activities this month</span>
          <span style={{ fontSize: '7px', color: '#007bff' }}>Past Goals</span>
        </div>
      </GoalCard>
      <ElevBtn><span>&#9650;</span> Request Level Elevation</ElevBtn>
    </Body>
    <NavBar><NavDot /><NavDot /><NavDot /><NavDot $active /></NavBar>
  </>
);

// ─── Screen 6: Share ─────────────────────────────────────────
const ShareCardBg = styled.div`
  background: ${theme.colors.deepNavy};
  border-radius: 10px;
  padding: 12px 10px;
  color: white;
  text-align: center;
`;

const ShareTitle = styled.div`
  font-family: ${theme.typography.headingFont};
  font-size: 9px;
  font-weight: 700;
  color: ${theme.colors.brandYellow};
  margin-bottom: 5px;
  letter-spacing: 1px;
`;

const ShareActTitle = styled.div`
  font-family: ${theme.typography.headingFont};
  font-size: 10px;
  font-weight: 700;
  margin-bottom: 5px;
`;

const ShareText = styled.div`
  font-family: ${theme.typography.bodyFont};
  font-size: 7.5px;
  line-height: 1.4;
  opacity: 0.85;
`;

const ShareMeta = styled.div`
  font-size: 7px;
  margin-top: 6px;
  opacity: 0.45;
`;

const SBtnRow = styled.div`
  display: flex;
  gap: 4px;
  margin-top: 7px;
`;

const SBtn = styled.div`
  flex: 1;
  padding: 6px 4px;
  background: white;
  border: 1px solid #eee;
  border-radius: 6px;
  font-family: ${theme.typography.bodyFont};
  font-size: 7px;
  color: ${theme.colors.deepNavy};
  text-align: center;
  font-weight: 600;
`;

const CaptionBox = styled.div`
  padding: 7px 9px;
  background: white;
  border: 1px solid #ddd;
  border-radius: 8px;
  font-family: ${theme.typography.bodyFont};
  font-size: 7px;
  color: #555;
  line-height: 1.4;
  margin-top: 6px;
`;

const Screen6 = () => (
  <>
    <TopBar>
      <span style={{ position: 'absolute', right: '12px', fontSize: '12px', color: theme.colors.slateGray }}>&#10005;</span>
      <BarTitle>Share Your Activity</BarTitle>
    </TopBar>
    <Body $gap="0">
      <div style={{ fontSize: '8px', color: theme.colors.slateGray, marginBottom: '5px', fontWeight: 600 }}>Card Preview</div>
      <ShareCardBg>
        <ShareTitle>&#10022; LIGHTWAY &#10022;</ShareTitle>
        <ShareActTitle>Organized winter supply drive</ShareActTitle>
        <ShareText>
          Connected with 15 community members and collected coats, blankets, and hygiene kits for the downtown shelter.
        </ShareText>
        <ShareMeta>Homelessness Awareness &middot; Daylight &middot; Maya J.</ShareMeta>
      </ShareCardBg>
      <SBtnRow>
        <SBtn>Copy Text</SBtn>
        <SBtn>Download</SBtn>
        <SBtn>Copy Link</SBtn>
      </SBtnRow>
      <div style={{ fontSize: '8px', color: theme.colors.slateGray, marginTop: '8px', fontWeight: 600 }}>Caption</div>
      <CaptionBox>
        I organized a winter supply drive for our local shelter! Collected coats, blankets and hygiene kits for 50+ people. #Lightway #HomelessnessAwareness #Advocacy
      </CaptionBox>
    </Body>
  </>
);

// ─── Steps Data ───────────────────────────────────────────────
const DATA = [
  { n: 1, t: 'Choose Your Causes', p: 'Pick the causes close to your heart — homelessness, community beautification, or human trafficking awareness. Choose one or all three.', S: Screen1 },
  { n: 2, t: 'Set Your Level', p: "Start where you're comfortable. From personal awareness (Streetlight) to collaborative community work (Daylight) to strategic systems change (Sunshield) — no wrong level, no pressure to move up.", S: Screen2 },
  { n: 3, t: 'Log Your Advocacy', p: 'Did something that matters? Log it in seconds. Add a photo, a reflection, attach a document. Your advocacy, documented.', S: Screen3 },
  { n: 4, t: 'Track Your Progress', p: 'See your journey grow. Filter by cause or level, search past activities, and revisit your impact anytime.', S: Screen4 },
  { n: 5, t: 'Set Goals & View Stats', p: 'Set monthly goals for each cause, track your advocacy stats, and request level elevation when you\'re ready to step up.', S: Screen5 },
  { n: 6, t: 'Share Your Impact', p: 'Create a branded share card and inspire others. Copy, download, or share on social media — or keep it for your portfolio.', S: Screen6 },
];

// ─── CTA Section ──────────────────────────────────────────────
const CTA = styled.section`
  background: ${theme.colors.deepNavy};
  text-align: center;
  padding: 48px 20px;
  position: relative;
  &::before {
    content: '';
    position: absolute;
    top: -1px;
    left: 0;
    right: 0;
    height: 36px;
    background: ${theme.colors.background};
    clip-path: ellipse(52% 100% at 50% 0%);
  }
`;

const CTAH = styled.h2`
  font-family: ${theme.typography.headingFont};
  font-size: 22px;
  font-weight: 700;
  color: white;
  margin-bottom: 8px;
`;

const CTAP = styled.p`
  font-family: ${theme.typography.bodyFont};
  font-size: 14px;
  color: rgba(255,255,255,0.7);
  margin-bottom: 24px;
  line-height: 1.5;
`;

const CTABtn = styled.button`
  padding: 14px 36px;
  background: ${theme.colors.brandYellow};
  color: ${theme.colors.deepNavy};
  font-family: ${theme.typography.headingFont};
  font-size: 14px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 1px;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  box-shadow: 0 4px 16px rgba(255,209,0,0.3);
  transition: transform 0.2s, box-shadow 0.2s;
  &:hover { transform: translateY(-2px); box-shadow: 0 6px 20px rgba(255,209,0,0.4); }
  &:active { transform: translateY(0); }
`;

const CTALink = styled.span`
  display: block;
  margin-top: 16px;
  font-family: ${theme.typography.bodyFont};
  font-size: 13px;
  color: rgba(255,255,255,0.55);
  text-decoration: underline;
  cursor: pointer;
  &:hover { color: white; }
`;

const Footer = styled.footer`
  background: ${theme.colors.deepNavy};
  text-align: center;
  padding: 0 20px 24px;
  font-family: ${theme.typography.bodyFont};
  font-size: 11px;
  color: rgba(255,255,255,0.3);
`;

// ─── Main Component ───────────────────────────────────────────
const HowItWorksScreen = () => {
  const navigate = useNavigate();

  return (
    <Page>
      <Hero>
        <HeroLogo src="/logo192.png" alt="Lightway" />
        <HeroH1>How <Gold>Lightway</Gold> Works</HeroH1>
        <HeroSub>Log your advocacy. Track your impact. Inspire others. In six simple steps.</HeroSub>
      </Hero>

      <Steps>
        <Pill><PillTag>6 Simple Steps</PillTag></Pill>
        {DATA.map(({ n, t, p, S }, i) => (
          <StepWrap key={n} $d={`${0.3 + i * 0.08}s`}>
            <StepHead>
              <Num>{n}</Num>
              <StepInfo>
                <StepH>{t}</StepH>
                <StepP>{p}</StepP>
              </StepInfo>
            </StepHead>
            <Phone>
              <Screen><S /></Screen>
              <HomeBar />
            </Phone>
          </StepWrap>
        ))}
      </Steps>

      <CTA>
        <CTAH>Ready to Start?</CTAH>
        <CTAP>Your advocacy matters. Start documenting it today.</CTAP>
        <CTABtn onClick={() => navigate('/signup')}>Create Your Account</CTABtn>
        <CTALink onClick={() => navigate('/login')}>Already have an account? Log in</CTALink>
      </CTA>
      <Footer>&copy; {new Date().getFullYear()} Light &amp; Cover &middot; lightandcover.org</Footer>
    </Page>
  );
};

export default HowItWorksScreen;
